// Buchhaltung — Kreditoren (Lieferanten-Eingangsrechnungen): 3-Wege-Abgleich,
// SEPA-Export mit Skonto-Automatik, „mit Skonto"-Zahlung.
import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Check, AlertTriangle, X, Plus, Trash2, Banknote, Wand2, Download } from "lucide-react";
import { toast } from "sonner";
import api, { formatPrice } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { fmtDate } from "./shared";
import { ScanInboxPanel } from "../../components/buchhaltung/ScanInboxPanel";

// Jul-2026 (User-Wunsch) — Rechnungskontrolle: Dienstleister-Lieferscheine
// (z. B. TÜV) ohne Wareneingang, zum Abgleich mit der eingehenden Rechnung.
function ControlNotesBlock() {
    const [data, setData] = useState(null);
    const load = useCallback(() => {
        api.get("/buchhaltung/kreditoren/control-notes").then((r) => setData(r.data)).catch(() => setData({ items: [] }));
    }, []);
    useEffect(() => { load(); }, [load]);
    const items = data?.items || [];
    if (items.length === 0) return null;
    const badge = (st) => st === "open"
        ? <span className="text-[10px] font-bold uppercase bg-amber-50 text-amber-800 border border-amber-200 rounded-sm px-1.5 py-0.5">Wartet auf Rechnung</span>
        : st === "matched"
            ? <span className="text-[10px] font-bold uppercase bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-sm px-1.5 py-0.5">Rechnung eingegangen</span>
            : <span className="text-[10px] font-bold uppercase bg-gray-100 text-gray-500 border border-gray-200 rounded-sm px-1.5 py-0.5">Erledigt</span>;
    const setStatus = async (n, status) => {
        try { await api.patch(`/buchhaltung/kreditoren/control-notes/${n.id}`, { status }); load(); }
        catch (e) { toast.error(e.response?.data?.detail || "Aktualisierung fehlgeschlagen"); }
    };
    return (
        <div className="bg-white border border-sky-200 rounded-sm" data-testid="control-notes-block">
            <div className="px-4 py-2.5 border-b border-sky-200 bg-sky-100 dark:bg-sky-950 dark:border-sky-800">
                <span className="font-bold text-sm text-sky-950 dark:text-sky-100">Rechnungskontrolle — Lieferscheine ohne Wareneingang ({data.open_count} offen)</span>
                <p className="text-[11px] text-sky-900 dark:text-sky-200 mt-0.5">Gescannte Dienstleister-Belege (z. B. TÜV): beim Eintreffen der Rechnung mit gleicher Lieferschein-Nr. wird automatisch abgeglichen.</p>
            </div>
            <ul className="divide-y divide-gray-100">
                {items.slice(0, 12).map((n) => (
                    <li key={n.id} className="px-4 py-2 flex items-center gap-2 text-xs flex-wrap" data-testid={`control-note-${n.id}`}>
                        <span className="font-bold">{n.supplier_name || "Unbekannter Aussteller"}</span>
                        {n.delivery_note_number && <span className="font-mono-num text-gray-500">LS {n.delivery_note_number}</span>}
                        <span className="text-gray-400">{fmtDate(n.created_at)}</span>
                        {badge(n.status)}
                        <span className="ml-auto flex items-center gap-2">
                            {n.file_url && <a href={n.file_url} target="_blank" rel="noopener noreferrer" className="underline text-gray-500 hover:text-gray-700" data-testid={`control-note-view-${n.id}`}>Beleg ansehen</a>}
                            {n.status !== "done" && (
                                <button type="button" onClick={() => setStatus(n, "done")} className="underline text-gray-500 hover:text-gray-700" data-testid={`control-note-done-${n.id}`}>Erledigt</button>
                            )}
                            {n.status === "done" && (
                                <button type="button" onClick={() => setStatus(n, "open")} className="underline text-gray-400 hover:text-gray-600" data-testid={`control-note-reopen-${n.id}`}>Wieder öffnen</button>
                            )}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}
import KiBuchungssatzModal from "./KiBuchungssatzModal";

const ReconBadge = ({ state = "off", label, title }) => {
    const cls = state === "ok"
        ? "bg-emerald-50 text-emerald-700 border-emerald-300"
        : state === "warn"
            ? "bg-amber-50 text-amber-700 border-amber-300"
            : "bg-gray-100 text-gray-400 border-gray-300";
    const Icon = state === "ok" ? Check : state === "warn" ? AlertTriangle : X;
    return (
        <span title={title} className={`inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${cls}`}>
            <Icon className="w-2.5 h-2.5" /> {label}
        </span>
    );
};

export default function KreditorenTab() {
    const [data, setData] = useState(null);
    const [sel, setSel] = useState(() => new Set());
    const [showCreate, setShowCreate] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [statusFilter, setStatusFilter] = useState("open");
    const [applySkonto, setApplySkonto] = useState(true);
    const [kiBeleg, setKiBeleg] = useState(null);  // Kreditor für KI-Buchungssatz-Modal
    const [searchParams] = useSearchParams();
    const focusId = searchParams.get("focus");
    const focusRef = useRef(null);

    // Deep-Link „→ zur Buchhaltung" aus dem Lieferschein-Listing: passende
    // Rechnung sichtbar machen (Alle) + hervorheben + hinscrollen.
    useEffect(() => { if (focusId) setStatusFilter("all"); }, [focusId]);
    useEffect(() => {
        if (focusId && focusRef.current) {
            focusRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [focusId, data]);

    const downloadOriginal = async (kid) => {
        try {
            const res = await api.get(`/buchhaltung/kreditoren/${kid}/e-rechnung-original`, { responseType: "blob" });
            const u = window.URL.createObjectURL(res.data);
            const a = document.createElement("a"); a.href = u; a.download = "e-rechnung-original";
            document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(u);
        } catch { toast.error("Originalbeleg konnte nicht geladen werden"); }
    };

    // Jul-2026 (User-Wunsch, Verschlankung): Der E-Rechnung-Upload (XRechnung/
    // ZUGFeRD) wurde in den vereinten „Beleg-Scan (KI)"-Tab verlagert.

    const kiPrefill = (it) => ({
        buchungstext: [it.supplier_name, it.invoice_number ? `RG ${it.invoice_number}` : "", it.notes || ""]
            .filter(Boolean).join(" · ") || "Eingangsrechnung",
        betrag_brutto: it.amount_gross,
        ust_satz: 19,
        richtung: "ausgabe",
        kontenrahmen: "SKR03",
        geschaeftspartner: it.supplier_name,
        zahlungsart: it.status === "paid" ? "bank" : "offen",
        source_type: "kreditor",
        source_id: it.id,
    });

    const load = useCallback(async () => {
        try { const r = await api.get("/buchhaltung/kreditoren", { params: { status: statusFilter } }); setData(r.data); setSel(new Set()); }
        catch { setData({ items: [], summary: {} }); }
    }, [statusFilter]);
    useEffect(() => { load(); }, [load]);

    const toggle = (id) => setSel((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

    const pay = async (id, withSkonto = false) => {
        try {
            const r = await api.post(`/buchhaltung/kreditoren/${id}/pay`, withSkonto ? { with_skonto: true } : {});
            toast.success(r.data?.skonto_amount ? `Bezahlt — ${formatPrice(r.data.skonto_amount)} Skonto gespart.` : "Als bezahlt gebucht.");
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Buchen fehlgeschlagen."); }
    };
    const del = async (id) => {
        if (!window.confirm("Eingangsrechnung löschen?")) return;
        try { await api.delete(`/buchhaltung/kreditoren/${id}`); toast.success("Gelöscht."); await load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen."); }
    };
    const sepaExport = async () => {
        if (sel.size === 0) { toast.error("Bitte Rechnungen für den SEPA-Export auswählen."); return; }
        setExporting(true);
        try {
            const r = await api.post("/buchhaltung/kreditoren/sepa-export", { invoice_ids: Array.from(sel), apply_skonto: applySkonto }, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a"); a.href = url; a.download = `SEPA-Ueberweisung-${new Date().toISOString().slice(0, 10)}.xml`; a.click();
            URL.revokeObjectURL(url);
            const skonto = Number(r.headers?.["x-sepa-skonto-total"] || 0);
            toast.success(skonto > 0 ? `SEPA-Datei erstellt — ${formatPrice(skonto)} Skonto abgezogen.` : "SEPA-Datei erstellt.");
            await load();
        } catch (e) {
            let msg = "SEPA-Export fehlgeschlagen.";
            try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* noop */ }
            toast.error(msg);
        } finally { setExporting(false); }
    };

    if (!data) return <div className="text-center py-12 text-gray-400"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>;
    const items = data.items || [];
    const s = data.summary || {};

    return (
        <div className="space-y-4" data-testid="buchhaltung-kreditoren">
            <ScanInboxPanel />
            <ControlNotesBlock />
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-sm text-gray-600">
                    Offen: <strong className="font-mono-num">{formatPrice(s.total_open || 0)}</strong> · {s.count || 0} Rechnung(en){s.overdue_count ? <span className="text-red-600"> · {s.overdue_count} überfällig</span> : null}
                </div>
                <div className="flex items-center gap-2">
                    <div className="inline-flex rounded-sm border border-gray-300 overflow-hidden" data-testid="kreditoren-status-filter">
                        {[{ k: "open", l: "Offen" }, { k: "all", l: "Alle" }].map((o) => (
                            <button key={o.k} type="button" onClick={() => setStatusFilter(o.k)}
                                className={`px-3 h-9 text-sm font-bold transition-colors ${statusFilter === o.k ? "bg-[#111827] text-[#FFCC00]" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                                data-testid={`kreditoren-filter-${o.k}`}>
                                {o.l}
                            </button>
                        ))}
                    </div>
                    <label className="inline-flex items-center gap-1.5 text-[11px] font-bold text-gray-600 cursor-pointer select-none" title="Bei Zahlung innerhalb der Skontofrist den Skonto-Betrag automatisch abziehen." data-testid="kreditoren-skonto-toggle">
                        <input type="checkbox" className="w-3.5 h-3.5 accent-emerald-600" checked={applySkonto} onChange={(e) => setApplySkonto(e.target.checked)} />
                        Skonto abziehen
                    </label>
                    <Button type="button" onClick={sepaExport} disabled={exporting || sel.size === 0} variant="outline" className="h-9 rounded-sm text-sm font-bold" data-testid="kreditoren-sepa-btn">
                        {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Banknote className="w-4 h-4 mr-1.5" />}
                        SEPA-Export{sel.size > 0 ? ` (${sel.size})` : ""}
                    </Button>
                    {/* Jul-2026: E-Rechnung-Upload wanderte in den vereinten
                        „Beleg-Scan (KI)"-Tab (XML wird dort direkt importiert). */}
                    <Button type="button" onClick={() => setShowCreate(true)} className="h-9 rounded-sm bg-[#111827] text-[#FFCC00] hover:bg-black text-sm font-bold" data-testid="kreditoren-create-btn">
                        <Plus className="w-4 h-4 mr-1.5" /> Eingangsrechnung
                    </Button>
                </div>
            </div>

            {items.length === 0 ? (
                <div className="text-center py-12 text-gray-500 bg-white border border-gray-200 rounded-sm" data-testid="kreditoren-empty">Keine Lieferanten-Eingangsrechnungen erfasst.</div>
            ) : (
                <div className="bg-white border border-gray-200 rounded-sm overflow-auto" data-testid="kreditoren-table">
                    <table className="w-full text-[12px]">
                        <thead className="bg-gray-100 text-gray-600">
                            <tr>
                                <th className="px-3 py-2 w-8"></th>
                                <th className="text-left px-3 py-2">Lieferant</th>
                                <th className="text-left px-3 py-2">Rechnungsnr.</th>
                                <th className="text-left px-3 py-2">Fällig</th>
                                <th className="text-right px-3 py-2">Offen</th>
                                <th className="text-left px-3 py-2">Abgleich</th>
                                <th className="text-right px-3 py-2">Aktion</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((it) => (
                                <tr key={it.id} ref={it.id === focusId ? focusRef : null}
                                    className={`border-t border-gray-100 hover:bg-gray-50 ${it.id === focusId ? "ring-2 ring-[#FFCC00] ring-inset bg-[#FFFBEB]" : ""}`}
                                    data-testid={`kreditoren-row-${it.id}`}>
                                    <td className="px-3 py-2 text-center">
                                        {it.status !== "paid" && (
                                            <input type="checkbox" className="w-4 h-4 accent-[#FFCC00]" checked={sel.has(it.id)} onChange={() => toggle(it.id)}
                                                disabled={!it.supplier_has_iban} title={it.supplier_has_iban ? "" : "Lieferant ohne IBAN"} data-testid={`kreditoren-select-${it.id}`} />
                                        )}
                                    </td>
                                    <td className="px-3 py-2 truncate max-w-[180px]">{it.supplier_name}</td>
                                    <td className="px-3 py-2 font-mono-num">
                                        {it.invoice_number || "—"}
                                        {it.source === "e-rechnung" && (
                                            <>
                                                <span className="ml-1.5 inline-flex items-center rounded-sm bg-blue-100 text-blue-700 px-1 py-0.5 text-[9px] font-bold align-middle" title={`Importiert: ${it.e_rechnung_format || "E-Rechnung"}${it.archived_at ? " · revisionssicher archiviert" : ""}`} data-testid={`kreditoren-erechnung-badge-${it.id}`}>
                                                    E-RECHNUNG{it.three_way_match === "matched" ? " ✓" : ""}
                                                </span>
                                                {it.archive_path && (
                                                    <button type="button" onClick={() => downloadOriginal(it.id)} className="ml-1 text-blue-600 hover:text-blue-800 align-middle" title="Originalbeleg herunterladen (GoBD-Archiv)" data-testid={`kreditoren-erechnung-download-${it.id}`}>
                                                        <Download className="w-3.5 h-3.5 inline" />
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </td>
                                    <td className={`px-3 py-2 ${it.days_overdue > 0 && it.status !== "paid" ? "text-red-600 font-bold" : ""}`}>{fmtDate(it.due_date)}{it.days_overdue > 0 && it.status !== "paid" ? ` (${it.days_overdue}T)` : ""}</td>
                                    <td className="px-3 py-2 text-right font-mono-num font-bold">
                                        {it.status === "paid"
                                            ? <span className="text-emerald-600">bezahlt{it.skonto_applied ? <span className="block text-[9px] font-bold text-emerald-700">−{formatPrice(it.skonto_amount || 0)} Skonto</span> : null}</span>
                                            : <>
                                                {formatPrice(it.open_amount)}
                                                {it.skonto?.eligible && (
                                                    <span className="block text-[9px] font-bold text-emerald-700" title={`Skonto ${it.skonto.percent}% bis ${fmtDate(it.skonto.deadline)} — zahlbar ${formatPrice(it.skonto.payable)}`} data-testid={`kreditoren-skonto-hint-${it.id}`}>
                                                        Skonto {it.skonto.percent}% bis {fmtDate(it.skonto.deadline)} (−{formatPrice(it.skonto.amount)})
                                                    </span>
                                                )}
                                            </>}
                                    </td>
                                    <td className="px-3 py-2">
                                        {(() => {
                                            const rc = it.reconciliation || {};
                                            const lsState = rc.lieferschein_state || (rc.lieferschein ? "ok" : "off");
                                            const lsTitle = lsState === "ok" ? (it.delivery_note_number || "Vollständig geliefert")
                                                : lsState === "partial" ? `Teilgeliefert${rc.delivery_note_count ? ` · ${rc.delivery_note_count} Lieferschein(e)` : ""}: ${it.delivery_note_number || ""}`
                                                    : "Kein Lieferschein";
                                            const am = rc.amount_match;
                                            const rgState = am === "matched" || am === "no_reference" ? "ok" : (am === "partial" || am === "over" || am === "mismatch") ? "warn" : "off";
                                            const rgLabel = am === "partial" ? "RG Teil" : am === "over" ? "RG +" : "RG";
                                            const rgTitle = am === "partial"
                                                ? `Teilrechnung: ${formatPrice(it.amount_gross)} von ${formatPrice(rc.po_total_gross)}${rc.po_fully_invoiced ? " · Bestellung vollständig fakturiert" : ` · bisher fakturiert ${formatPrice(rc.po_invoiced_total)}`}`
                                                : am === "over"
                                                    ? `Mehrbetrag: ${formatPrice(it.amount_gross)} statt ${formatPrice(rc.po_total_gross)} (Diff. ${formatPrice(rc.amount_diff)})`
                                                    : am === "matched"
                                                        ? `Betrag stimmt mit Bestellung ${rc.purchase_order_number || ""} überein`
                                                        : "Manuell erfasst (keine Bestellung verknüpft)";
                                            return (
                                                <div className="flex items-center gap-1 flex-wrap" data-testid={`kreditoren-recon-${it.id}`}>
                                                    <ReconBadge state={lsState === "partial" ? "warn" : lsState} label={lsState === "partial" ? "LS Teil" : "LS"} title={lsTitle} />
                                                    <ReconBadge state={rgState} label={rgLabel} title={rgTitle} />
                                                    <ReconBadge state={rc.zahlung ? "ok" : "off"} label="ZA" title={rc.zahlung ? "Zahlung gebucht" : "Noch nicht bezahlt"} />
                                                    {(() => {
                                                        // Aug-2026 (User-Wunsch) — Abgleich mit gebuchten Lieferschein-SCANS, farbig
                                                        const dm = it.dn_scan_match || {};
                                                        if (dm.status === "ok") return (
                                                            <span className="inline-flex items-center gap-0.5 text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-800 border border-emerald-300"
                                                                title={`${dm.scan_count} gescannte(r) Beleg(e): ${(dm.numbers || []).join(", ")}${dm.scans_total_gross != null ? ` · Scan-Summe ${formatPrice(dm.scans_total_gross)}` : ""}${dm.diff != null ? ` · Differenz ${formatPrice(dm.diff)}` : ""}`}
                                                                data-testid={`kreditoren-dnscan-ok-${it.id}`}>
                                                                <Check className="w-2.5 h-2.5" /> Scan ✓
                                                            </span>
                                                        );
                                                        if (dm.status === "deviation") return (
                                                            <span className="inline-flex items-center gap-0.5 text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-red-100 text-red-700 border border-red-300"
                                                                title={`Betragsabweichung zur Scan-Summe: Rechnung ${formatPrice(it.amount_gross)} · gescannt ${formatPrice(dm.scans_total_gross)} (${dm.scan_count} Beleg(e): ${(dm.numbers || []).join(", ")})`}
                                                                data-testid={`kreditoren-dnscan-dev-${it.id}`}>
                                                                Scan Δ {formatPrice(Math.abs(dm.diff || 0))}
                                                            </span>
                                                        );
                                                        return (
                                                            <span className="inline-flex items-center text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-gray-100 text-gray-500 border border-gray-200"
                                                                title="Kein gescannter Lieferschein/Beleg zu dieser Rechnung gefunden (Abgleich über Lieferant + Beleg-Nr.)"
                                                                data-testid={`kreditoren-dnscan-missing-${it.id}`}>
                                                                Scan —
                                                            </span>
                                                        );
                                                    })()}
                                                    {rc.match_status === "full" && (
                                                        <span className="inline-flex items-center gap-0.5 text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-emerald-600 text-white" data-testid={`kreditoren-match-full-${it.id}`}>
                                                            <Check className="w-2.5 h-2.5" /> 3-Wege
                                                        </span>
                                                    )}
                                                    {rc.amount_match === "partial" && rc.po_fully_invoiced && (
                                                        <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-300" title="Alle Teilrechnungen dieser Bestellung zusammen decken die Bestellsumme." data-testid={`kreditoren-po-invoiced-${it.id}`}>
                                                            Bestellung vollst. fakturiert
                                                        </span>
                                                    )}
                                                    {it.buchungssatz && (
                                                        <span className="inline-flex items-center gap-0.5 text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-[#111827] text-[#FFCC00]" title={`Gebucht: Soll ${it.buchungssatz.soll_konto} an Haben ${it.buchungssatz.haben_konto} · BU ${it.buchungssatz.steuerschluessel || "-"}`} data-testid={`kreditoren-gebucht-${it.id}`}>
                                                            <Check className="w-2.5 h-2.5" /> Gebucht {it.buchungssatz.soll_konto}→{it.buchungssatz.haben_konto}
                                                        </span>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="flex items-center justify-end gap-1.5">
                                            <Button type="button" onClick={() => setKiBeleg(it)} variant="outline" className="h-7 rounded-sm text-[11px] font-bold border-[#FFCC00] bg-yellow-50 text-[#111827] hover:bg-yellow-100" title="KI-Buchungssatz vorschlagen" data-testid={`kreditoren-ki-${it.id}`}>
                                                <Wand2 className="w-3.5 h-3.5 mr-1" /> Buchungssatz
                                            </Button>
                                            {it.status !== "paid" && it.skonto?.eligible && (
                                                <Button type="button" onClick={() => pay(it.id, true)} variant="outline" className="h-7 rounded-sm text-[11px] font-bold border-emerald-400 bg-emerald-50 text-emerald-800 hover:bg-emerald-100" title={`Zahlbetrag ${formatPrice(it.skonto.payable)} (−${it.skonto.percent}% Skonto)`} data-testid={`kreditoren-pay-skonto-${it.id}`}>
                                                    − Skonto
                                                </Button>
                                            )}
                                            {it.status !== "paid" && (
                                                <Button type="button" onClick={() => pay(it.id)} variant="outline" className="h-7 rounded-sm text-[11px] font-bold border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid={`kreditoren-pay-${it.id}`}>
                                                    Bezahlt
                                                </Button>
                                            )}
                                            <button type="button" onClick={() => del(it.id)} className="text-gray-400 hover:text-red-600" aria-label="Löschen" data-testid={`kreditoren-del-${it.id}`}>
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {showCreate && <KreditorCreateModal onClose={() => setShowCreate(false)} onDone={() => { setShowCreate(false); load(); }} />}
            {kiBeleg && <KiBuchungssatzModal prefill={kiPrefill(kiBeleg)} onClose={() => setKiBeleg(null)} onBooked={load} />}
        </div>
    );
}

function KreditorCreateModal({ onClose, onDone }) {
    const [suppliers, setSuppliers] = useState([]);
    const [pos, setPos] = useState([]);
    const [form, setForm] = useState({ supplier_id: "", purchase_order_id: "", invoice_number: "", amount_gross: "", invoice_date: new Date().toISOString().slice(0, 10), due_date: "", delivery_note_number: "", notes: "" });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/suppliers").then((r) => setSuppliers(Array.isArray(r.data) ? r.data : (r.data?.items || []))).catch(() => setSuppliers([]));
    }, []);
    const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    // Offene Bestellungen des gewählten Lieferanten für die Verknüpfung laden
    useEffect(() => {
        if (!form.supplier_id) { setPos([]); return; }
        api.get("/buchhaltung/kreditoren/open-purchase-orders", { params: { supplier_id: form.supplier_id } })
            .then((r) => setPos(r.data?.items || []))
            .catch(() => setPos([]));
        setForm((p) => ({ ...p, purchase_order_id: "" }));
    }, [form.supplier_id]);

    const selectPo = (pid) => {
        const po = pos.find((p) => p.id === pid);
        setForm((p) => ({
            ...p,
            purchase_order_id: pid,
            amount_gross: po && !p.amount_gross ? String(po.total_gross) : p.amount_gross,
            delivery_note_number: po?.delivery_note_number && !p.delivery_note_number ? po.delivery_note_number : p.delivery_note_number,
        }));
    };

    const submit = async () => {
        if (!form.supplier_id) { toast.error("Bitte Lieferant wählen."); return; }
        if (!form.amount_gross || Number(form.amount_gross) <= 0) { toast.error("Bitte Betrag eingeben."); return; }
        setSaving(true);
        try {
            await api.post("/buchhaltung/kreditoren", { ...form, amount_gross: Number(form.amount_gross) });
            toast.success("Eingangsrechnung erfasst.");
            onDone?.();
        } catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen."); }
        finally { setSaving(false); }
    };

    const selPo = pos.find((p) => p.id === form.purchase_order_id);
    const amtNum = Number(form.amount_gross || 0);
    const poTol = selPo ? Math.max(1, selPo.total_gross * 0.01) : 0;
    const poPartial = selPo && amtNum > 0 && amtNum < selPo.total_gross - poTol;   // Teilrechnung (ok)
    const poOver = selPo && amtNum > selPo.total_gross + poTol;                     // Mehrbetrag (Warnung)

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center p-4 overflow-y-auto" data-testid="kreditor-create-modal">
            <div className="w-full max-w-lg mt-12 bg-white border border-gray-200 rounded-sm shadow-xl">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-[#111827] text-white rounded-t-sm">
                    <h2 className="font-display text-lg font-black">Neue Eingangsrechnung (Kreditor)</h2>
                    <button type="button" onClick={onClose} className="p-1 rounded-sm hover:bg-white/10" data-testid="kreditor-create-close"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-5 space-y-3">
                    <div>
                        <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Lieferant *</label>
                        <select value={form.supplier_id} onChange={(e) => set("supplier_id", e.target.value)} className="w-full h-9 text-sm rounded-sm border border-gray-300 px-2 mt-1 focus:border-[#111827] focus:outline-none" data-testid="kreditor-supplier">
                            <option value="">— Lieferant wählen —</option>
                            {suppliers.map((s) => <option key={s.id} value={s.id}>{`${s.name}${s.iban ? " · IBAN ✓" : " · keine IBAN"}`}</option>)}
                        </select>
                    </div>
                    {form.supplier_id && (
                        <div>
                            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Bestellung verknüpfen (3-Wege-Abgleich)</label>
                            <select value={form.purchase_order_id} onChange={(e) => selectPo(e.target.value)} className="w-full h-9 text-sm rounded-sm border border-gray-300 px-2 mt-1 focus:border-[#111827] focus:outline-none disabled:bg-gray-100" disabled={pos.length === 0} data-testid="kreditor-purchase-order">
                                <option value="">{pos.length === 0 ? "— keine offene Bestellung —" : "— keine Verknüpfung —"}</option>
                                {pos.map((p) => <option key={p.id} value={p.id}>{`${p.order_number} · ${formatPrice(p.total_gross)}${p.delivery_note_number ? ` · LS ${p.delivery_note_number}` : ""}`}</option>)}
                            </select>
                            {selPo && <p className="text-[10px] text-gray-500 mt-1">Bestellsumme: <strong>{formatPrice(selPo.total_gross)}</strong> — der Rechnungsbetrag wird damit abgeglichen.</p>}
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Rechnungsnr.</label>
                            <Input value={form.invoice_number} onChange={(e) => set("invoice_number", e.target.value)} className="h-9 rounded-sm mt-1" data-testid="kreditor-invoice-number" />
                        </div>
                        <div>
                            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Betrag (brutto) *</label>
                            <Input type="number" step="0.01" value={form.amount_gross} onChange={(e) => set("amount_gross", e.target.value)} className="h-9 rounded-sm mt-1" data-testid="kreditor-amount" />
                        </div>
                        <div>
                            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Rechnungsdatum</label>
                            <Input type="date" value={form.invoice_date} onChange={(e) => set("invoice_date", e.target.value)} className="h-9 rounded-sm mt-1" data-testid="kreditor-invoice-date" />
                        </div>
                        <div>
                            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Fällig (optional)</label>
                            <Input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} className="h-9 rounded-sm mt-1" data-testid="kreditor-due-date" />
                        </div>
                    </div>
                    <div>
                        <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Lieferschein-Nr. (Abgleich)</label>
                        <Input value={form.delivery_note_number} onChange={(e) => set("delivery_note_number", e.target.value)} placeholder="z. B. LS-1234" className="h-9 rounded-sm mt-1" data-testid="kreditor-delivery-note" />
                    </div>
                    {poPartial && (
                        <div className="flex items-start gap-1.5 text-[11px] text-blue-700 bg-blue-50 border border-blue-300 rounded-sm px-2.5 py-2" data-testid="kreditor-po-partial">
                            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                            <span><strong>Teilrechnung:</strong> {formatPrice(amtNum)} von {formatPrice(selPo.total_gross)} der Bestellung. Wird als „RG Teil" markiert — weitere Teilrechnungen sind möglich.</span>
                        </div>
                    )}
                    {poOver && (
                        <div className="flex items-start gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-300 rounded-sm px-2.5 py-2" data-testid="kreditor-po-mismatch">
                            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                            <span>Der Rechnungsbetrag <strong>{formatPrice(amtNum)}</strong> übersteigt die Bestellsumme <strong>{formatPrice(selPo.total_gross)}</strong> (Diff. {formatPrice(amtNum - selPo.total_gross)}). Die Rechnung wird als „Mehrbetrag" markiert.</span>
                        </div>
                    )}
                </div>
                <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200 bg-gray-50 rounded-b-sm">
                    <Button type="button" variant="outline" onClick={onClose} className="h-9 rounded-sm text-sm" data-testid="kreditor-cancel">Abbrechen</Button>
                    <Button type="button" onClick={submit} disabled={saving} className="h-9 rounded-sm bg-[#111827] text-[#FFCC00] hover:bg-black text-sm font-bold" data-testid="kreditor-submit">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null} Speichern
                    </Button>
                </div>
            </div>
        </div>
    );
}
